import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';
import { getFirstTouchUtm } from '@/lib/utm';
import { siteRoute } from '@/lib/domains';
import '@/styles/nymphe-v3.css';
import '@/styles/access.css';

const API = process.env.REACT_APP_BACKEND_URL;

const JOINING_AS_OPTIONS = [
  'Trans woman',
  'Femme',
  'Admirer',
  'Other',
  'Prefer not to say',
];

// Email validation mirror of the server-side rule.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const isValidEmail = (raw) => {
  if (typeof raw !== 'string') return false;
  const v = raw.trim();
  if (!v) return false;
  if (v.length > 254) return false;
  return EMAIL_RE.test(v);
};

const ease = [0.22, 1, 0.36, 1];
const TRANSITION = { duration: 0.2, ease };

const AccessPage = () => {
  // step: 1 = hero landing, 2 = form
  const [step, setStep] = useState(1);
  // formStep: inside step 2 — 1 = email, 2 = details
  const [formStep, setFormStep] = useState(1);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [preferredName, setPreferredName] = useState('');
  const [joiningAs, setJoiningAs] = useState('');
  const [city, setCity] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [alreadyOnList, setAlreadyOnList] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const focusFiredRef = useRef(false);

  // ─── Silent UTM capture (no UI effect) ───────────────────────────────
  // Read on first mount so it survives even if the URL changes later.
  // Preference: sessionStorage first-touch → current URL fallback.
  const utmRef = useRef({ utm_source: '', utm_campaign: '', utm_medium: '' });
  useEffect(() => {
    utmRef.current = getFirstTouchUtm();
  }, []);

  const [familiar] = useState(() => {
    try { return localStorage.getItem('nymphe_access_seen') === 'true'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('nymphe_access_seen', 'true'); } catch { /* ignore */ }
  }, []);

  // Fire waitlist_form_opened once when the form is actually visible.
  useEffect(() => {
    if (step === 2 && !success && !alreadyOnList) {
      track('waitlist_form_opened');
    }
  }, [step, success, alreadyOnList]);

  const selectJoiningAs = (value) => {
    setJoiningAs((prev) => (prev === value ? '' : value));
  };

  const handleEmailFocus = () => {
    if (!focusFiredRef.current) {
      focusFiredRef.current = true;
      track('request_access_form_start');
    }
  };

  // Step 1 → Step 2: validate email and advance.
  const handleStep1Submit = (e) => {
    if (e) e.preventDefault();
    const normalized = (email || '').trim().toLowerCase();
    if (!isValidEmail(normalized)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setEmailError('');
    setFormStep(2);
    track('request_access_step_1_complete');
    // Capture the partial lead the moment Step 1 is completed. Fire and
    // forget — the user must never wait on this. The backend dedupes by
    // email and never overwrites an existing record.
    const utm = utmRef.current || {};
    const trimmedReferral = (referralCode || '').trim();
    axios
      .post(`${API}/api/waitlist/partial`, {
        email: normalized,
        referral_code: trimmedReferral || undefined,
        utm_source: utm.utm_source || undefined,
        utm_campaign: utm.utm_campaign || undefined,
        utm_medium: utm.utm_medium || undefined,
      })
      .catch(() => {});
  };

  // Step 2: final submit — requires consent checkbox.
  const handleFinalSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!consent) return;
    const normalized = (email || '').trim().toLowerCase();
    if (!isValidEmail(normalized)) {
      setEmailError('Please enter a valid email address.');
      setFormStep(1);
      return;
    }
    setLoading(true);
    setSubmitError('');
    const trimmedReferral = (referralCode || '').trim();
    const utm = utmRef.current || {};
    try {
      const response = await axios.post(`${API}/api/waitlist`, {
        email: normalized,
        identities: joiningAs ? [joiningAs] : [],
        name: preferredName || undefined,
        city: city || undefined,
        referral_code: trimmedReferral || undefined,
        utm_source:   utm.utm_source   || undefined,
        utm_campaign: utm.utm_campaign || undefined,
        utm_medium:   utm.utm_medium   || undefined,
      });
      if (response.data && response.data.success) {
        setSuccess(true);
        track('request_access_submit_success', {
          has_referral_code: Boolean(trimmedReferral),
          has_utm_source:    Boolean(utm.utm_source),
          has_utm_campaign:  Boolean(utm.utm_campaign),
          has_utm_medium:    Boolean(utm.utm_medium),
        });
        track('waitlist_submitted');
        toast.success("You're on the list.");
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Lead', {
            content_name: 'Access Request',
            content_category: 'Access Request',
          });
        }
      } else if (response.data && /already/i.test(response.data.message || '')) {
        setAlreadyOnList(true);
        track('request_access_submit_error', { reason: 'already_on_list' });
      } else {
        setSubmitError("Something didn't submit cleanly. Please check your details and try again.");
        track('request_access_submit_error', { reason: 'server_message' });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.message || '';
      if (/email/i.test(String(detail))) {
        setEmailError('Please enter a valid email address.');
        setFormStep(1);
      } else {
        setSubmitError("Something didn't submit cleanly. Please check your details and try again.");
      }
      track('request_access_submit_error', { reason: 'network_or_validation' });
    }
    setLoading(false);
  };

  return (
    <div className="acc" data-testid="access-page">
      <div className="acc-bg">
        <picture>
          <source media="(max-width: 720px)" srcSet="/images/access-bg-v2-mobile.jpg" />
          <img src="/images/access-bg-v2.jpg" alt="" />
        </picture>
        <div className="acc-bg-overlay" />
        <div className="acc-bg-scrim" aria-hidden="true" />
      </div>
      <div className="noise-overlay" />

      <AnimatePresence mode="wait">
        {step === 1 && !success && !alreadyOnList && (
          <motion.section
            key="step1"
            className="acc-step"
            data-testid="access-step1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: familiar ? 0.4 : 0.8 }}
          >
            <div className="acc-content">
              {familiar && (
                <motion.p
                  className="acc-success-micro"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                  style={{ marginBottom: 20 }}
                >
                  Welcome back.
                </motion.p>
              )}
              <motion.h1
                className="acc-headline"
                initial={{ opacity: 0, y: familiar ? 10 : 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: familiar ? 0.15 : 0.4, duration: familiar ? 0.6 : 1, ease }}
              >
                Request Early Access
              </motion.h1>
              <motion.p
                className="acc-sub"
                initial={{ opacity: 0, y: familiar ? 6 : 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: familiar ? 0.28 : 0.65, duration: familiar ? 0.5 : 0.9, ease }}
              >
                NYMPHÉ is opening in limited waves.
              </motion.p>
              <motion.p
                className="acc-body"
                data-testid="access-hero-body"
                initial={{ opacity: 0, y: familiar ? 6 : 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: familiar ? 0.36 : 0.78, duration: familiar ? 0.5 : 0.9, ease }}
              >
                Share a few details to join the access list. As the platform grows, we&rsquo;ll release access gradually and send instructions when your wave opens.
              </motion.p>

              <motion.div
                className="acc-steps"
                data-testid="access-what-happens-next"
                initial={{ opacity: 0, y: familiar ? 6 : 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: familiar ? 0.4 : 0.85, duration: familiar ? 0.5 : 0.9, ease }}
                aria-label="What happens next"
              >
                <p className="acc-steps-label">What happens next</p>
                <ol className="acc-steps-list">
                  <li><span className="acc-steps-num" aria-hidden="true">01</span><span className="acc-steps-text">Send your request.</span></li>
                  <li><span className="acc-steps-num" aria-hidden="true">02</span><span className="acc-steps-text">We review it privately.</span></li>
                  <li><span className="acc-steps-num" aria-hidden="true">03</span><span className="acc-steps-text">Access is released in limited waves as the platform grows.</span></li>
                  <li><span className="acc-steps-num" aria-hidden="true">04</span><span className="acc-steps-text">You&rsquo;ll receive access instructions.</span></li>
                  <li><span className="acc-steps-num" aria-hidden="true">05</span><span className="acc-steps-text">Create or complete your profile.</span></li>
                </ol>
              </motion.div>

              <motion.p
                className="acc-waves-note"
                data-testid="access-waves-note"
                initial={{ opacity: 0, y: familiar ? 4 : 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: familiar ? 0.5 : 1.0, duration: familiar ? 0.5 : 0.8, ease }}
              >
                Access is released in limited waves while NYMPHÉ is in its early stages.
              </motion.p>

              <motion.button
                className="acc-btn"
                data-testid="access-request-btn"
                onClick={() => setStep(2)}
                initial={{ opacity: 0, y: familiar ? 6 : 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: familiar ? 0.6 : 1.15, duration: familiar ? 0.5 : 0.8, ease }}
              >
                Request Access
              </motion.button>

              <motion.p
                className="acc-reassurance"
                data-testid="access-reassurance"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: familiar ? 0.75 : 1.3, duration: 0.7 }}
              >
                Your request is reviewed privately. Your details are never public, sold, or shared outside the access process.
              </motion.p>

              <motion.p
                className="acc-adults-only"
                data-testid="access-adults-only"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: familiar ? 0.9 : 1.45, duration: 0.7 }}
              >
                NYMPHÉ is an adults-only space. 18+ only.
              </motion.p>

              <motion.p
                className="acc-support-link"
                data-testid="access-support-link"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: familiar ? 1.0 : 1.6, duration: 0.7 }}
              >
                Privacy or safety question?{' '}
                <a
                  href={siteRoute('/contact')}
                  className="acc-support-link-a"
                  data-testid="access-support-link-anchor"
                >
                  Write to us
                </a>
                .
              </motion.p>
            </div>
          </motion.section>
        )}

        {step === 2 && !success && !alreadyOnList && (
          <motion.section
            key="step2"
            className="acc-step"
            data-testid="access-step2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="acc-content acc-content--form">
              <p className="acc-progress" data-testid="access-progress" aria-live="polite">
                Step {formStep} of 2
              </p>

              <AnimatePresence mode="wait">
                {formStep === 1 && (
                  <motion.div
                    key="form-step-1"
                    initial={{ opacity: 0, x: 8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -8 }}
                    transition={TRANSITION}
                  >
                    <h2 className="acc-form-title" data-testid="access-form-title">
                      Step inside, softly.
                    </h2>
                    <p className="acc-form-sub">
                      Start with your email. We&rsquo;ll only use it to manage your access request.
                    </p>
                    <form
                      className="acc-form"
                      onSubmit={handleStep1Submit}
                      noValidate
                    >
                      <div className={`acc-field ${emailError ? 'acc-field--error' : ''}`}>
                        <label htmlFor="access-email" className="acc-field-label">
                          Email address <span className="acc-field-req" aria-hidden="true">*</span>
                        </label>
                        <input
                          id="access-email"
                          type="email"
                          className={`acc-input ${emailError ? 'acc-input--error' : ''}`}
                          placeholder="your@email.com"
                          value={email}
                          onFocus={handleEmailFocus}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            if (emailError) setEmailError('');
                          }}
                          onBlur={() => {
                            if (email && !isValidEmail(email)) {
                              setEmailError('Please enter a valid email address.');
                            }
                          }}
                          autoComplete="email"
                          inputMode="email"
                          aria-invalid={!!emailError}
                          aria-describedby={emailError ? 'access-email-error' : 'access-email-helper'}
                          aria-required="true"
                          data-testid="access-email"
                        />
                        <p id="access-email-helper" className="acc-field-helper">
                          We&rsquo;ll use this to send your private invitation.
                        </p>
                        {emailError && (
                          <p
                            id="access-email-error"
                            className="acc-field-error"
                            data-testid="access-email-error"
                            role="alert"
                          >
                            {emailError}
                          </p>
                        )}
                      </div>

                      <button
                        type="submit"
                        className="acc-btn"
                        data-testid="access-continue"
                      >
                        Continue
                      </button>

                      <p className="acc-microcopy" data-testid="access-microcopy">
                        Requesting access is free. This does not create a public profile.
                      </p>
                    </form>
                  </motion.div>
                )}

                {formStep === 2 && (
                  <motion.div
                    key="form-step-2"
                    initial={{ opacity: 0, x: 8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -8 }}
                    transition={TRANSITION}
                  >
                    <h2 className="acc-form-title" data-testid="access-form-title">
                      A few quiet details.
                    </h2>
                    <p className="acc-form-sub">
                      These details are optional, but help us prepare the right invitation waves.
                    </p>

                    <form
                      className="acc-form"
                      onSubmit={handleFinalSubmit}
                      noValidate
                    >
                      <div className="acc-field">
                        <label htmlFor="access-preferred-name" className="acc-field-label">
                          Preferred name
                        </label>
                        <input
                          id="access-preferred-name"
                          type="text"
                          className="acc-input"
                          value={preferredName}
                          onChange={(e) => setPreferredName(e.target.value)}
                          autoComplete="given-name"
                          data-testid="access-preferred-name"
                        />
                        <p className="acc-field-helper">
                          Optional. Use the name you&rsquo;d like us to address you by.
                        </p>
                      </div>

                      <div className="acc-field">
                        <span className="acc-field-label">I&rsquo;m joining as</span>
                        <div className="acc-pills" data-testid="joining-as-pills" role="group" aria-label="I'm joining as">
                          {JOINING_AS_OPTIONS.map((opt) => (
                            <button
                              key={opt}
                              type="button"
                              className={`acc-pill ${joiningAs === opt ? 'acc-pill--active' : ''}`}
                              onClick={() => selectJoiningAs(opt)}
                              data-testid={`pill-${opt.toLowerCase().replace(/\s/g, '-')}`}
                              aria-pressed={joiningAs === opt}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                        <p className="acc-field-helper">Optional.</p>
                      </div>

                      <div className="acc-field">
                        <label htmlFor="access-city" className="acc-field-label">
                          City
                        </label>
                        <input
                          id="access-city"
                          type="text"
                          className="acc-input"
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          autoComplete="address-level2"
                          data-testid="access-city"
                        />
                        <p className="acc-field-helper">
                          Optional. Helps us plan invitation waves.
                        </p>
                      </div>

                      <div className="acc-field">
                        <label htmlFor="access-referral-code" className="acc-field-label">
                          Promo or referral code
                        </label>
                        <input
                          id="access-referral-code"
                          type="text"
                          className="acc-input"
                          value={referralCode}
                          onChange={(e) => setReferralCode(e.target.value)}
                          placeholder="Optional code"
                          autoComplete="off"
                          autoCapitalize="characters"
                          spellCheck="false"
                          data-testid="access-referral-code"
                        />
                        <p className="acc-field-helper">
                          Optional. Use this only if you were given a code through a NYMPHÉ promotion or creator.
                        </p>
                      </div>

                      <div className="acc-consent">
                        <label className="acc-consent-label">
                          <input
                            type="checkbox"
                            className="acc-consent-input"
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                            data-testid="access-consent"
                            aria-required="true"
                          />
                          <span className="acc-consent-box" aria-hidden="true" />
                          <span className="acc-consent-text">
                            I confirm I am 18 or older and agree to the{' '}
                            <a href={siteRoute('/privacy')} target="_blank" rel="noopener noreferrer">Privacy Policy</a>,{' '}
                            <a href={siteRoute('/terms')} target="_blank" rel="noopener noreferrer">Terms</a>, and{' '}
                            <a href={siteRoute('/safety')} target="_blank" rel="noopener noreferrer">Community Standards</a>.
                          </span>
                        </label>
                      </div>

                      <p className="acc-privacy-note" data-testid="access-privacy-note">
                        We&rsquo;ll only use your details to manage your access request and contact you about NYMPHÉ early access. We do not sell or rent your personal information.
                      </p>

                      {submitError && (
                        <p
                          className="acc-field-error acc-field-error--block"
                          data-testid="access-submit-error"
                          role="alert"
                        >
                          {submitError}
                        </p>
                      )}

                      <div className="acc-form-actions">
                        <button
                          type="button"
                          className="acc-btn-back"
                          onClick={() => setFormStep(1)}
                          data-testid="access-back"
                        >
                          Back
                        </button>
                        <button
                          type="submit"
                          className="acc-btn"
                          disabled={loading || !consent}
                          data-testid="access-submit"
                        >
                          {loading ? 'Submitting…' : 'Request My Access'}
                        </button>
                      </div>
                    </form>

                    <p
                      className="acc-billing-note"
                      data-testid="access-billing-note"
                    >
                      Early access is complimentary to request. Full membership unlocks once your wave opens.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              <p
                className="acc-policy-notice"
                data-testid="access-policy-notice"
              >
                To protect user trust, privacy, and platform integrity, NYMPHÉ does not permit personal use of the platform by anyone involved in operating it, or by their immediate family members, domestic partners, or household members. Internal access is limited to operational, moderation, safety, legal, and testing purposes only.
              </p>
            </div>
          </motion.section>
        )}

        {alreadyOnList && (
          <motion.section
            key="already"
            className="acc-step"
            data-testid="access-already"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="acc-content">
              <motion.h2
                className="acc-headline"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8, ease }}
              >
                You&rsquo;re already on the access list.
              </motion.h2>
              <motion.p
                className="acc-sub"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.7, ease }}
              >
                No need to request twice &mdash; we&rsquo;ll be in touch when your invite is ready.
              </motion.p>
              <motion.a
                href={siteRoute('/')}
                className="acc-btn-return"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9, duration: 0.6 }}
                data-testid="access-return-home"
              >
                Return home
              </motion.a>
            </div>
          </motion.section>
        )}

        {success && (
          <motion.section
            key="success"
            className="acc-step"
            data-testid="access-success"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            <div className="acc-content">
              <motion.h2
                className="acc-headline"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.9, ease }}
              >
                You&rsquo;re on the list.
              </motion.h2>
              <motion.p
                className="acc-sub"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8, ease }}
              >
                Private invitations are released in limited waves &mdash; keep an eye on your inbox at{' '}
                <span className="acc-sub-email" data-testid="access-success-email">{email}</span>.
              </motion.p>
              <motion.p
                className="acc-success-micro"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9, duration: 0.7 }}
              >
                Quietly curated. Sent when it&rsquo;s time.
              </motion.p>
              <motion.a
                href={siteRoute('/')}
                className="acc-btn-return"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.2, duration: 0.6 }}
                data-testid="access-return-home"
              >
                Return home
              </motion.a>
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AccessPage;
